import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import RichText from "./RichText";
import MediaPicker from "./MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const inputCls =
  "w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20";

export default function PageEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ax.get(`/pages-list`).then(({ data }) => {
      const page = (data || []).find((p) => (p._key || p.slug) === slug);
      if (!page) toast.error("Page not found");
      setDoc(page || null);
    });
  }, [slug]);

  if (doc === null) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;
  if (!doc) return <div className="p-8 text-navy-700">Page not found.</div>;

  const setField = (path, value) => {
    const next = JSON.parse(JSON.stringify(doc));
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setDoc(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { _id, _key, ...payload } = doc;
      const { data } = await ax.put(`/pages/${slug}/_`, payload);
      setDoc(data);
      toast.success("Page saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <Link to="/admin/pages" className="inline-flex items-center gap-1.5 text-sm text-navy-600 hover:text-teal">
        <ArrowLeft className="h-4 w-4" /> All pages
      </Link>
      <h1 className="mt-4 font-display font-bold text-3xl text-navy-800">Edit page: <span className="text-teal">{slug}</span></h1>

      <div className="mt-8 grid gap-5">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">SEO title</span>
          <input
            value={doc.seo?.title || ""}
            onChange={(e) => setField("seo.title", e.target.value)}
            className={inputCls}
            data-testid="page-seo-title"
          />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">SEO description</span>
          <textarea
            value={doc.seo?.description || ""}
            onChange={(e) => setField("seo.description", e.target.value)}
            rows={3}
            className={inputCls}
            data-testid="page-seo-description"
          />
        </label>

        {/* Render rich body fields (any *_html or 'body' key) */}
        {Object.keys(doc).filter((k) => k.endsWith("_html") || k === "body").map((k) => (
          <div key={k}>
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{k}</span>
            <RichText
              value={doc[k] || ""}
              onChange={(html) => setField(k, html)}
              placeholder="Write the page body…"
              testId={`page-rich-${k}`}
            />
          </div>
        ))}

        {!Object.keys(doc).some((k) => k.endsWith("_html") || k === "body") && (
          <div>
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">body_html (add rich content for this page)</span>
            <RichText
              value=""
              onChange={(html) => setField("body_html", html)}
              placeholder="Write the page body…"
              testId="page-rich-body_html"
            />
          </div>
        )}

        {/* Hero image */}
        <div>
          <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Hero image</span>
          {doc.hero_media?.url ? (
            <div className="flex items-center gap-4">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${doc.hero_media.url}`} alt={doc.hero_media.alt || ""} className="h-24 w-40 object-cover rounded-xl border border-navy-100" />
              <button type="button" onClick={() => setPickerOpen(true)} className="text-xs font-semibold text-teal hover:underline">Change</button>
              <button type="button" onClick={() => setField("hero_media", null)} className="text-xs font-semibold text-navy-500 hover:text-red-600">Remove</button>
            </div>
          ) : (
            <button type="button" onClick={() => setPickerOpen(true)} className="px-4 py-2.5 rounded-xl border border-dashed border-navy-200 text-navy-700 text-sm font-semibold hover:border-teal hover:text-teal">
              Choose hero image
            </button>
          )}
          {pickerOpen && (
            <MediaPicker kind="image" onClose={() => setPickerOpen(false)} onSelect={(m) => { setField("hero_media", m); setPickerOpen(false); }} />
          )}
        </div>
      </div>

      <div className="mt-10 flex justify-end gap-3">
        <Link to="/admin/pages" className="px-5 py-2.5 text-sm font-semibold text-navy-700 hover:text-teal">Cancel</Link>
        <button onClick={save} disabled={busy} data-testid="page-editor-save"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save page
        </button>
      </div>
    </div>
  );
}
